/**
 * lib/track.jsx — Audience-track lock.
 *
 * Per founder direction (Feb 14): "I don't like everyone having access
 * to the modules. Only community has access to their modules. Student
 * has access to their own. CNA Academy is only for CNA information.
 * BLS/ACLS only for their respective info. Close the site off from
 * other information once an option has been selected."
 *
 * Behavior:
 *   • Picking a track on the umbrella hub locks it in localStorage.
 *   • Non-track routes redirect to the user's track home.
 *   • Master admin key (`localStorage.maldek_admin_unlocked === ADMIN_KEY`)
 *     bypasses every wall — used by faculty / hospital admins / QA.
 *   • Footer "switch track" button clears the lock.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isMasterUnlocked } from "./masterUnlock";

const LS_KEY = "maldek_track_lock_v1";
const LS_ADMIN = "maldek_admin_unlocked";

// Phase-1 hardening: the master key is no longer compiled into the
// bundle. Gates that used to compare against this literal now POST
// the user's input to /api/auth/master-bootstrap (server-side check).
// Anything still calling isMasterKey() will get a false from any
// non-empty string, which is the safe default until callers migrate.
const ADMIN_KEY = "";
export const MASTER_KEY = ADMIN_KEY;

export function isMasterKey(/* input */) {
  // No client-side validation. Use the server bootstrap endpoint
  // (/api/auth/master-bootstrap) for any real promotion.
  return false;
}

/** Force-set the admin/master flag in localStorage. Called by any gate
 *  whose user enters the master key — lets them roam freely across all
 *  tracks afterward without having to also visit the umbrella admin form. */
export function persistMasterUnlock() {
  try { localStorage.setItem(LS_ADMIN, MASTER_KEY); } catch { /* silent */ }
}

// Per-track route allow-list. Any route prefix listed here is reachable
// once that track is locked. Marketing/billing/SEO pages are listed under
// `PUBLIC_ALWAYS` and reachable from any track.
export const TRACK_ALLOW = {
  community: [
    "/", "/community", "/community/proof", "/start-pilot", "/sign-in", "/institution-admin", "/explain", "/symptoms", "/lab-reference",
    "/med-library", "/safety-net", "/cooking", "/therapeutics",
    "/imaging-procedures", "/pathophys",
    "/walkthrough",
  ],
  student: [
    "/", "/clinical-reasoning", "/reasoning", "/explain", "/symptoms",
    "/pathophys", "/lab-reference", "/imaging-procedures", "/med-library",
    "/therapeutics", "/cooking", "/safety-net",
    "/time-machine", "/patient", "/nclex", "/walkthrough",
    "/for-schools", "/how-it-works",
  ],
  cna: [
    "/", "/cna-academy", "/cna-admin", "/workforce", "/employer",
    "/bls-academy", "/acls-academy",
    "/how-it-works",
  ],
  bls: [
    "/", "/bls-academy", "/acls-academy", "/cme", "/how-it-works",
  ],
};

// Routes always reachable regardless of track (marketing + admin surfaces).
// Admin surfaces have their OWN passcode gates so opening them while
// track-locked is safe — the inner gate still blocks unauthorized users.
export const PUBLIC_ALWAYS = [
  "/", "/business-plan", "/legacy-landing", "/for-institutions",
  "/for-hospitals", "/legal", "/privacy", "/terms",
  "/cna-admin", "/employer", "/bls-admin", "/cme",
  "/acls-academy", "/concept", "/how-it-works",
  "/explain-short", "/explain-short-layouts", "/explain-long-layouts", "/community-library-layouts",
];

const TrackContext = createContext({
  track: null,
  setTrack: () => {},
  clearTrack: () => {},
  isAdminUnlocked: false,
  unlockAdmin: () => false,
  isAllowed: () => true,
});

export function TrackProvider({ children }) {
  const [track, setTrackState] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || null; }
    catch { return null; }
  });
  const [adminKey, setAdminKey] = useState(() => {
    try { return localStorage.getItem(LS_ADMIN) || ""; }
    catch { return ""; }
  });

  const setTrack = useCallback((t) => {
    setTrackState(t);
    try {
      if (t) localStorage.setItem(LS_KEY, t);
      else localStorage.removeItem(LS_KEY);
    } catch { /* silent */ }
  }, []);

  const clearTrack = useCallback(() => setTrack(null), [setTrack]);

  const unlockAdmin = useCallback((key) => {
    if (key === ADMIN_KEY) {
      try { localStorage.setItem(LS_ADMIN, key); } catch { /* silent */ }
      setAdminKey(key);
      return true;
    }
    return false;
  }, []);

  // Admin bypass — three ways to qualify:
  //   1. adminKey === ADMIN_KEY (legacy compare, "" === "" for fresh visitors —
  //      preserves the current dormant-wall behavior for regular users);
  //   2. any non-empty stored maldek_admin_unlocked value — it could only have
  //      been written by the OLD flow after verifying the real master key, so
  //      the Phase-1 hardening (ADMIN_KEY="") must not strand those devices
  //      behind the track wall (founder devices bounced off /clinical-reasoning);
  //   3. the site-wide master unlock flag set by /auth/master-verify.
  const isAdminUnlocked = adminKey === ADMIN_KEY || Boolean(adminKey) || isMasterUnlocked();

  const isAllowed = useCallback((pathname) => {
    if (isAdminUnlocked) return true;
    if (!track) return true; // no lock yet — everything visible until they pick
    if (PUBLIC_ALWAYS.some((p) => pathname === p || (p !== "/" && pathname.startsWith(p)))) return true;
    const allow = TRACK_ALLOW[track] || [];
    return allow.some((p) => pathname === p || (p !== "/" && pathname.startsWith(p + "/")) || pathname === p);
  }, [track, isAdminUnlocked]);

  const value = useMemo(() => ({
    track, setTrack, clearTrack, isAdminUnlocked, unlockAdmin, isAllowed,
  }), [track, setTrack, clearTrack, isAdminUnlocked, unlockAdmin, isAllowed]);

  return <TrackContext.Provider value={value}>{children}</TrackContext.Provider>;
}

export function useTrack() {
  return useContext(TrackContext);
}

/** Guard wrapper — redirects off-track navigations back to umbrella hub.
 *  Mount once near the top of <Routes>. */
export function useTrackGuard(navigate, pathname) {
  const { isAllowed, track } = useTrack();
  useEffect(() => {
    if (!isAllowed(pathname)) {
      // Send back to hub; the hub will offer to switch tracks.
      navigate("/?off_track=1", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, track]);
}
